import {
  AlignCenterOutlined,
  AlignLeftOutlined,
  AlignRightOutlined,
  DeleteOutlined
} from "@ant-design/icons"
import { Button, Flex, Form, Popconfirm, Segmented, Skeleton, Tooltip, Typography } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React, { type JSX, Suspense, lazy } from "react"

import type { TemplateDocumentFormData } from "../../../../types/template-document-form-data.tsx"
import { type DocumentTemplateBlock } from "../../../../types/types.ts"
import { type BlockColumnConfig } from "./TemplateDocumentFormColumnOptions.tsx"
import { TextInput } from "../../../../../../components/Input/TextInput.tsx"

const TemplateDocumentFormColumnOptions = lazy(
  () => import("./TemplateDocumentFormColumnOptions.tsx")
)

export interface BlockVariant {
  label: string
  titleReadonly?: boolean
  required?: boolean
  alignment?: boolean
  columns?: BlockColumnConfig[]
  component:
    | JSX.Element
    | ((
        props: { block: DocumentTemplateBlock; disabled?: boolean },
        index: number
      ) => JSX.Element)
}

interface TemplateDocumentFormItemProps
  extends Pick<
    BlockVariant,
    "required" | "alignment" | "titleReadonly" | "label" | "columns"
  > {
  id: string
  index: number
  form: FormInstance<TemplateDocumentFormData>
  onRemove: (id: string) => void
}

export function TemplateDocumentFormLabel({
  id,
  index,
  label,
  required,
  alignment,
  titleReadonly,
  columns,
  form,
  onRemove
}: TemplateDocumentFormItemProps) {
  return (
    <Flex
      justify="space-between"
      align="center"
      className="w-full"
      gap={"small"}
    >
      {titleReadonly ? (
        <Typography.Title level={5} className="my-0">
          {label}
        </Typography.Title>
      ) : (
        <Form.Item
          name={["blocks", index, "config", "title"]}
          className="mb-0 w-full"
          initialValue={label}
          rules={[
            { required: true, whitespace: true, message: "" },
            { max: 100, message: "Max 100 characters" }
          ]}
        >
          <TextInput className={"w-full"} />
        </Form.Item>
      )}

      <Flex gap={8} align="center">
        {alignment && (
          <Form.Item
            name={["blocks", index, "config", "alignment"]}
            className="mb-0"
            initialValue="left"
          >
            <Segmented
              onClick={(e) => e.stopPropagation()}
              options={[
                {
                  value: "left",
                  icon: (
                    <Tooltip title="Left align">
                      <AlignLeftOutlined />
                    </Tooltip>
                  )
                },
                {
                  value: "center",
                  icon: (
                    <Tooltip title="Center align">
                      <AlignCenterOutlined />
                    </Tooltip>
                  )
                },
                {
                  value: "right",
                  icon: (
                    <Tooltip title="Right align">
                      <AlignRightOutlined />
                    </Tooltip>
                  )
                }
              ]}
            />
          </Form.Item>
        )}

        {!!columns?.length && (
          <Suspense fallback={<Skeleton.Button shape={"square"} active />}>
            <TemplateDocumentFormColumnOptions
              form={form}
              columns={columns}
              index={index}
            />
          </Suspense>
        )}

        {!required && (
          <Popconfirm
            okType={"danger"}
            title={"Confirm Delete"}
            description={"Are you sure you want to delete this block?"}
            onConfirm={(e) => {
              e?.stopPropagation()
              onRemove(id)
            }}
            okText="Yes"
            cancelText="No"
          >
            <Button variant={"outlined"} danger icon={<DeleteOutlined />} />
          </Popconfirm>
        )}
      </Flex>
    </Flex>
  )
}
